import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Folder, Plus, Search, MoreHorizontal, Edit2, Trash2, FolderOpen, StickyNote, Image, FileText, X } from 'lucide-react'
import { Folder as FolderIcon } from '@phosphor-icons/react'
import { useAuthStore } from '../stores/authStore'
import { motion, AnimatePresence } from 'framer-motion'
import ConfirmationModal from '../components/ConfirmationModal'
import AnimatedFolder from '../components/AnimatedFolder'
import ContentCard from '../components/ContentCard'
import AssetViewerModal from '../components/AssetViewerModal'
import NoteEditorModal, { type NoteEditorAsset } from '../components/NoteEditorModal'

interface SpaceItem {
  id: string
  title: string
  type?: string
  image?: string
  metadata?: any
}

interface Space {
  id: string
  name: string
  description?: string
  color: string
  icon: string
  itemCount: number
  created_at: string
  updated_at: string
  previewItems?: SpaceItem[]
}

export default function SpacesPage() {
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const [spaces, setSpaces] = useState<Space[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [newSpaceName, setNewSpaceName] = useState('')
  const [loading, setLoading] = useState(false) // Start false to prevent flash
  const [initialLoadDone, setInitialLoadDone] = useState(false) // Track first load
  const [error, setError] = useState<string | null>(null)
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; space: Space | null }>({
    isOpen: false,
    space: null
  })
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null)
  const [spaceContent, setSpaceContent] = useState<any[]>([])
  const [loadingSpaceContent, setLoadingSpaceContent] = useState(false)
  
  // Windows-style inline editing
  const [editingSpaceId, setEditingSpaceId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')

  // Asset viewer modal state
  const [assetViewerOpen, setAssetViewerOpen] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<{
    id: string
    title: string
    type: 'note' | 'screenshot'
    content?: string
    imageUrl?: string
    sourceTitle?: string
    sourceUrl?: string
    createdAt?: string
    metadata?: any
  } | null>(null)

  // Fabric-like note editor state
  const [noteEditorOpen, setNoteEditorOpen] = useState(false)
  const [selectedNote, setSelectedNote] = useState<NoteEditorAsset | null>(null)
  
  // Main page note editor state (for FAB)
  const [mainNoteEditorOpen, setMainNoteEditorOpen] = useState(false)
  const [mainSelectedNote, setMainSelectedNote] = useState<NoteEditorAsset | null>(null)
  const [showSpeedDial, setShowSpeedDial] = useState(false)

  // Fetch spaces from API with preview items
  useEffect(() => {
    const fetchSpaces = async () => {
      if (!token) {
        setInitialLoadDone(true)
        return
      }
      
      try {
        // Only show loading on first load
        if (!initialLoadDone) {
          setLoading(true)
        }
        const response = await fetch('/api/spaces', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (!response.ok) {
          throw new Error('Failed to fetch spaces')
        }
        
        const data = await response.json()
        const spacesData = data.spaces || []
        
        // Fetch preview items for each space (first 3 items for the 3D folder cards)
        const spacesWithPreviews = await Promise.all(
          spacesData.map(async (space: Space) => {
            try {
              const itemsResponse = await fetch(`/api/spaces/${space.id}/items?limit=3`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              })
              
              if (itemsResponse.ok) {
                const itemsData = await itemsResponse.json()
                return {
                  ...space,
                  previewItems: (itemsData.items || []).slice(0, 3).map((item: any) => {
                    // Determine type
                    const itemType = item.metadata?.type || 
                      (item.tags?.includes('screenshot') ? 'screenshot' : 
                       item.tags?.includes('note') ? 'note' : 
                       item.url?.includes('youtube') ? 'youtube' : 'web')
                    
                    // Get image URL - screenshots have image_url in metadata
                    let imageUrl = ''
                    if (itemType === 'screenshot' && item.metadata?.image_url) {
                      imageUrl = item.metadata.image_url
                    } else if (item.metadata?.thumbnail) {
                      imageUrl = item.metadata.thumbnail
                    } else if (itemType === 'youtube' && item.url) {
                      // Extract YouTube thumbnail
                      const match = item.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)
                      if (match) {
                        imageUrl = `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`
                      }
                    }
                    
                    return {
                      id: item.id,
                      title: item.title || 'Untitled',
                      type: itemType,
                      image: imageUrl,
                      metadata: item.metadata
                    }
                  })
                }
              }
            } catch {
              // Ignore preview fetch errors
            }
            return { ...space, previewItems: [] }
          })
        )
        
        setSpaces(spacesWithPreviews)
      } catch (err) {
        console.error('Error fetching spaces:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch spaces')
      } finally {
        setLoading(false)
        setInitialLoadDone(true)
      }
    }

    fetchSpaces()
  }, [token])

  const handleCreateSpace = async () => {
    if (!token) return
    
    try {
      // Find a unique "New Space" name
      let spaceName = 'New Space'
      let counter = 1
      while (spaces.some(s => s.name === spaceName)) {
        counter++
        spaceName = `New Space ${counter}`
      }
      
      // Create space with unique default name
      const response = await fetch('/api/spaces', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: spaceName,
          color: 'blue',
          icon: 'folder'
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        console.error('Create space error:', errorData)
        throw new Error(errorData.error || 'Failed to create space')
      }
      
      const data = await response.json()
      const newSpace = data.space
      
      // Add to spaces list and immediately enter edit mode
      setSpaces([newSpace, ...spaces])
      setEditingSpaceId(newSpace.id)
      setEditingName(newSpace.name)
      setIsCreating(false)
    } catch (err) {
      console.error('Error creating space:', err)
      setError(err instanceof Error ? err.message : 'Failed to create space')
    }
  }

  const handleRenameSpace = async (spaceId: string, newName: string) => {
    if (!newName.trim() || !token) {
      // If empty, revert to original name
      const space = spaces.find(s => s.id === spaceId)
      if (space) {
        setEditingName(space.name)
      }
      setEditingSpaceId(null)
      return
    }
    
    try {
      const response = await fetch(`/api/spaces/${spaceId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newName.trim()
        })
      })
      
      if (!response.ok) {
        throw new Error('Failed to rename space')
      }
      
      // Update local state
      setSpaces(spaces.map(s => s.id === spaceId ? { ...s, name: newName.trim() } : s))
      setEditingSpaceId(null)
    } catch (err) {
      console.error('Error renaming space:', err)
      setError(err instanceof Error ? err.message : 'Failed to rename space')
      setEditingSpaceId(null)
    }
  }

  const handleDeleteSpace = async (space: Space) => {
    try {
      const response = await fetch(`/api/spaces/${space.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to delete space')
      }
      
      setSpaces(spaces.filter(s => s.id !== space.id))
      setDeleteModal({ isOpen: false, space: null })
    } catch (err) {
      console.error('Error deleting space:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete space')
    }
  }

  const openDeleteModal = (space: Space) => {
    setDeleteModal({ isOpen: true, space })
  }

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, space: null })
  }

  const handleSpaceClick = async (space: Space) => {
    setSelectedSpace(space)
    await fetchSpaceContent(space.id)
  }

  const fetchSpaceContent = async (spaceId: string) => {
    if (!token) return
    
    try {
      setLoadingSpaceContent(true)
      const response = await fetch(`/api/spaces/${spaceId}/items`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setSpaceContent(data.items || [])
      }
    } catch (error) {
      console.error('Error fetching space content:', error)
    } finally {
      setLoadingSpaceContent(false)
    }
  }

  const goBackToSpaces = () => {
    setSelectedSpace(null)
    setSpaceContent([])
  }

  const filteredSpaces = spaces.filter(space =>
    space.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // If a space is selected, show the space content view
  if (selectedSpace) {
    return (
      <div className="max-w-7xl mx-auto p-8">
        {/* Space Header */}
        <div className="mb-8">
          <div className="mb-6">
            {/* Breadcrumb Navigation */}
            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
              <button
                onClick={goBackToSpaces}
                className="hover:text-gray-700 transition-colors"
              >
                Spaces
              </button>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-gray-900 font-medium">{selectedSpace.name}</span>
            </nav>
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{selectedSpace.name}</h1>
              <p className="text-gray-600 mt-1">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  {selectedSpace.itemCount} items in this space
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Space Content */}
        {loadingSpaceContent ? (
          <div className="flex items-center justify-center py-12">
            <span className="text-gray-500 dark:text-gray-400 font-medium">Loading content...</span>
          </div>
        ) : spaceContent.length === 0 ? (
          <div className="text-center py-12">
            <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No content yet</h3>
            <p className="text-gray-600 mb-6">
              This space is empty. Go to your Library to move content here.
            </p>
            <button
              onClick={() => navigate('/library')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              Go to Library
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {spaceContent.map((item) => {
              // Get thumbnail for YouTube or screenshots
              let thumbnail = item.metadata?.thumbnail || item.metadata?.image_url
              if (!thumbnail && item.url?.includes('youtube')) {
                const match = item.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)
                if (match) thumbnail = `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`
              }
              
              // Detect item type
              const isNote = item.metadata?.type === 'note' || item.tags?.includes('note') || item.title?.startsWith('Note:')
              const isScreenshot = item.metadata?.type === 'screenshot' || item.tags?.includes('screenshot')
              
              return (
                <ContentCard
                key={item.id}
                  id={item.id}
                  title={item.title}
                  summary={isNote ? item.content : (item.summary || item.content)}
                  url={item.url}
                  thumbnail={thumbnail}
                  createdAt={item.created_at || item.createdAt || item.metadata?.created_at}
                  category={item.category}
                  duration={item.metadata?.duration}
                  metadata={item.metadata}
                  tags={item.tags}
                onClick={() => {
                    if (isNote) {
                      // Open note in Fabric-like editor
                      setSelectedNote({
                        id: item.id,
                        title: item.title,
                        content: item.content || item.summary || '',
                        createdAt: item.created_at || item.createdAt || item.metadata?.created_at,
                        related_to_content_id: item.related_to_content_id || item.metadata?.source_content_id || null,
                        metadata: item.metadata
                      })
                      setNoteEditorOpen(true)
                    } else if (isScreenshot) {
                      // Open screenshot in asset viewer
                      setSelectedAsset({
                        id: item.id,
                        title: item.title,
                        type: 'screenshot',
                        imageUrl: item.metadata?.image_url,
                        sourceTitle: item.metadata?.source_title,
                        sourceUrl: item.metadata?.source_url || item.url,
                        createdAt: item.created_at || item.createdAt || item.metadata?.created_at,
                        metadata: item.metadata
                      })
                      setAssetViewerOpen(true)
                    } else {
                      // Regular content - go to library
                  navigate(`/library?highlight=${item.id}`)
                    }
                  }}
                />
              )
            })}
                    </div>
                  )}
        
        {/* Asset Viewer Modal - must be inside this return block */}
        <AssetViewerModal
          isOpen={assetViewerOpen}
          onClose={() => {
            setAssetViewerOpen(false)
            setSelectedAsset(null)
          }}
          asset={selectedAsset}
          token={token || ''}
          onSave={async (id, content) => {
            // Refresh space content
            const spaceId = selectedSpace?.id
            if (spaceId) {
              fetchSpaceContent(spaceId)
            }
            return true
          }}
          onDeleted={() => {
            // Refresh space content after deletion
            const spaceId = selectedSpace?.id
            if (spaceId) {
              fetchSpaceContent(spaceId)
            }
          }}
        />

        {/* Fabric-like Note Editor Modal */} 
        <NoteEditorModal
          isOpen={noteEditorOpen}
          onClose={() => {
            setNoteEditorOpen(false)
            setSelectedNote(null)
          }}
          note={selectedNote}
          token={token || ''}
          onSaved={() => {
            const spaceId = selectedSpace?.id
            if (spaceId) {
              fetchSpaceContent(spaceId)
            }
          }}
          onDeleted={() => {
            const spaceId = selectedSpace?.id
            if (spaceId) {
              fetchSpaceContent(spaceId)
            }
          }}
        />
      </div>
    )
  }

  // Show loading state on first load - centered, no spinner
  if (loading && !initialLoadDone) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 font-medium">Loading your spaces...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-8">
        <div className="text-center py-12">
          <div className="text-red-500 mb-4">Error: {error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-8 dark:text-white">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
            <FolderIcon weight="duotone" className="w-6 h-6 text-gray-600 dark:text-gray-300" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">Spaces</h1>
            <p className="text-gray-500 dark:text-gray-400">{spaces.length} {spaces.length === 1 ? 'space' : 'spaces'}</p>
          </div>
        </div>
      </div>

      {/* Search Bar - matching Library page exact layout */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6 pr-0 lg:pr-96">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search spaces..."
            className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 shadow-sm"
          />
        </div>
        
        {/* Spacer to match Library's Select button width */}
        <div className="flex items-center gap-2">
          <div className="w-[68px]"></div>
        </div>
      </div>

      {/* Spaces Grid - 3D Animated Folders */}
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {filteredSpaces.map((space, index) => (
          <div key={space.id} className="relative group">
            {editingSpaceId === space.id ? (
              // Inline editing mode - Windows style
              <div className="relative">
                <AnimatedFolder
                  title={editingName}
                  projects={(space.previewItems || []).map(item => ({
                    id: item.id,
                    image: item.image || '',
                    title: item.title,
                    type: item.type
                  }))}
                  itemCount={space.itemCount}
                  color={space.color || 'blue'}
                  onClick={() => {}}
                />
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={() => handleRenameSpace(space.id, editingName)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleRenameSpace(space.id, editingName)
                    } else if (e.key === 'Escape') {
                      setEditingSpaceId(null)
                      setEditingName('')
                    }
                  }}
                  className="absolute bottom-2 left-2 right-2 px-2 py-1 text-xs font-medium text-center
                           bg-white dark:bg-gray-800 border border-blue-500 rounded
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  onFocus={(e) => e.target.select()}
                />
              </div>
            ) : (
              // Normal mode
              <>
                <AnimatedFolder
                  title={space.name}
                  projects={(space.previewItems || []).map(item => ({
                    id: item.id,
                    image: item.image || '',
                    title: item.title,
                    type: item.type
                  }))}
                  itemCount={space.itemCount}
                  color={space.color || 'blue'}
                  onClick={() => handleSpaceClick(space)}
                />
                {/* Edit button - appears on hover */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditingSpaceId(space.id)
                    setEditingName(space.name)
                  }}
                  className="absolute top-2 left-2 p-1.5 rounded-lg bg-white/80 dark:bg-gray-800/80 
                           text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20
                           opacity-0 group-hover:opacity-100 transition-all duration-200
                           border border-gray-200 dark:border-gray-700 shadow-sm"
                  title="Rename space"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {/* Delete button - appears on hover */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    openDeleteModal(space)
                  }}
                  className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/80 dark:bg-gray-800/80 
                           text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20
                           opacity-0 group-hover:opacity-100 transition-all duration-200
                           border border-gray-200 dark:border-gray-700 shadow-sm"
                  title="Delete space"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Empty State - with 3D folder hint */}
      {filteredSpaces.length === 0 && (
        <div className="text-center py-16">
          <div className="relative mx-auto mb-6 w-32 h-32">
            {/* 3D Empty Folder Illustration */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div 
                className="w-24 h-20 rounded-lg relative"
                style={{
                  background: 'linear-gradient(180deg, #93C5FD 0%, #3B82F6 100%)',
                  transform: 'perspective(500px) rotateX(10deg)',
                  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)'
                }}
              >
                <div 
                  className="absolute -top-3 left-2 w-10 h-4 rounded-t-lg"
                  style={{
                    background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                  }}
                />
              </div>
            </div>
          </div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">No spaces found</h3>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            {searchQuery ? 'Try a different search term' : 'Create your first space to organize your content into smart folders'}
          </p>
          {!searchQuery && (
            <button
              onClick={handleCreateSpace}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all font-medium shadow-lg shadow-blue-500/25"
            >
              <Plus className="w-4 h-4" />
              Create Space
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={() => deleteModal.space && handleDeleteSpace(deleteModal.space)}
        title="Delete Space"
        message={`Are you sure you want to delete "${deleteModal.space?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />

      {/* Floating Action Buttons - FAB on right side */}
      <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-3">
        <button
          onClick={() => setShowSpeedDial(true)}
          className="w-14 h-14 rounded-full bg-white/95 dark:bg-gray-900/95 backdrop-blur 
                     border border-gray-200/50 dark:border-gray-700/50
                     shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_8px_16px_-4px_rgba(0,0,0,0.1)]
                     dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_8px_16px_-4px_rgba(0,0,0,0.2)]
                     flex items-center justify-center text-gray-900 dark:text-white
                     hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors"
        >
          <Plus className="w-6 h-6" strokeWidth={2.5} />
        </button>
      </div>

      {/* Full-screen blurred overlay with create options */}
      <AnimatePresence>
        {showSpeedDial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            onClick={() => setShowSpeedDial(false)}
          >
            {/* Blurred backdrop */}
            <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/70 backdrop-blur-md" />
            
            {/* Centered options */}
            <div className="relative z-10 flex flex-col items-center gap-5" onClick={(e) => e.stopPropagation()}>
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.05 }}
                onClick={() => {
                  setShowSpeedDial(false)
                  setMainSelectedNote({
                    id: 'draft-' + Date.now(),
                    title: '',
                    content: '',
                    isDraft: true
                  })
                  setMainNoteEditorOpen(true)
                }}
                className="flex items-center gap-4 px-6 py-4 rounded-2xl hover:bg-gray-100/80 dark:hover:bg-gray-800/50 transition-colors w-64"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-700 flex items-center justify-center">
                  <StickyNote className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
                </div>
                <span className="text-base font-medium text-gray-900 dark:text-white">Note</span>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.1 }}
                onClick={() => { setShowSpeedDial(false); handleCreateSpace(); }}
                className="flex items-center gap-4 px-6 py-4 rounded-2xl hover:bg-gray-100/80 dark:hover:bg-gray-800/50 transition-colors w-64"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-700 flex items-center justify-center">
                  <Folder className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
                </div>
                <span className="text-base font-medium text-gray-900 dark:text-white">Space</span>
              </motion.button>

              {/* Close hint */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.15 }}
                onClick={() => setShowSpeedDial(false)}
                className="mt-4 w-12 h-12 rounded-full bg-gray-900 dark:bg-white flex items-center justify-center shadow-lg -ml-8"
              >
                <X className="w-5 h-5 text-white dark:text-gray-900" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Note Editor Modal for FAB */}
      <NoteEditorModal
        isOpen={mainNoteEditorOpen}
        onClose={() => {
          setMainNoteEditorOpen(false)
          setMainSelectedNote(null)
        }}
        note={mainSelectedNote}
        token={token || ''}
        onSaved={() => {
          // Refresh spaces to update item counts
          window.location.reload()
        }}
        onDeleted={() => {}}
      />
    </div>
  )
}

